import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import i18next from "i18next";
import axios from "axios";
import PulseLoader from "react-spinners/PulseLoader";
import { useNavigate } from "react-router-dom";
import { WarningPopUp, Image, Anchor } from "hesus-ui-kit";
import { renderPriceWithCurrencyWithoutUnit } from "@utils/utils";
import { getDateFormat } from "@utils/datePickerUtils";
import editIcon from "@icons/ic-edit-blue.svg";
import downloadIcon from "@icons/ic-download.svg";
import deleteIcon from "@icons/ic-delete-red.svg";
import viewIcon from "@icons/ic-view-dark-blue.svg";
import { invoiceStatusText, invoiceStatusColor } from "@utils/constants";
import { ColumnButton } from "@utils/extra-style";
import { GridRow, GridSubLabel, GridSubItem } from "../../InvoiceList.styled";
import "../../InvoiceList.scss";

const ListingData = (props) => {
  const history = useNavigate();
  const {
    t,
    invoiceData,
    isLoading,
    counter,
    setCounter,
    entityType,
    entityId,
  } = props;
  const [isConfirmVisible, setConfirmVisibility] = useState(false);
  const [isConfirm, setActionConfirmStatus] = useState(false);
  const [invoiceId, setInvoiceId] = useState("");

  const handleDisable = (id) => {
    setInvoiceId(id);
    setConfirmVisibility(true);
  };

  const onPopUpCancel = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(false);
  };

  const onPopUpConfirm = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(true);
  };
  useEffect(() => {
    if (isConfirm) {
      axios
        .delete(`/clientInvoices/${invoiceId}`)
        .then(() => {
          setInvoiceId("");
          setCounter(counter + 1);
        })
        .catch((error) => {
          setInvoiceId("");
          setCounter(counter + 1);
          handleApiError(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirm]);

  const onInvoiceEdit = (invoiceId) => {
    const navigationState =
      entityType === "company" && entityId ? { companyId: entityId } : null;
    entityType === "site" && entityId ? { siteId: entityId } : null;
    history(`/invoice/edit/${invoiceId}`, {
      state: navigationState,
    });
  };

  const onInvoiceView = (invoiceId) => {
    history(`/invoice/view/${invoiceId}`);
  };

  const onInvoiceDownload = (invoiceId) => {
    history(`/invoice/edit/${invoiceId}`);
  };

  return (
    <>
      {isLoading && (
        <div className="suggestions-list-loading">
          <PulseLoader color="#1bace4" size={15} />
        </div>
      )}
      {!isLoading && invoiceData?.length === 0 && (
        <GridRow>
          <GridSubLabel textAlign="center">{t("NO_DATA")}</GridSubLabel>
        </GridRow>
      )}
      {!isLoading &&
        invoiceData?.length > 0 &&
        invoiceData.map((invoice, invoiceIndex) => {
          return (
            <GridRow
              key={`invoice-list-${invoice.id}${invoiceIndex}`}
              column={
                ["company", "site"].includes(entityType)
                  ? "13.5% 13.5% 11.5% 11.5% 11.5% 15.5% 11.5% 11.5%"
                  : "12% 12% 12% 10% 10% 10% 14% 10% 10%"
              }
              marginBottom="0px"
              gap="1px"
              borderLeft="2px solid #767676"
              borderBottom="2px solid #767676"
              borderRight="1px solid #767676"
            >
              <GridSubItem
                color="#0A4986"
                fontStyle="normal"
                padding="5px"
                fontWeight="700"
                textAlign="left"
                vertical-align="middle"
                borderRight="2px solid rgb(118, 118, 118)"
              >
                {[0, 1].includes(invoice.status) && !invoice.documentNumber && (
                  <ColumnButton
                    border="#767676"
                    background="#767676"
                    marginRight="10px"
                  >
                    <span style={{ fontSize: "10px" }}>
                      {t("CLIENT_INVOICING_NUMBER_TEMP")}
                    </span>
                  </ColumnButton>
                )}
                <Anchor
                  className="invoice-list__link"
                  styles={{ fontWeight: "700" }}
                  url={`/invoice/view/${invoice.id}`}
                >
                  {invoice.documentNumber ?? invoice?.id}
                </Anchor>
              </GridSubItem>
              {entityType !== "company" && (
                <GridSubItem
                  color="#0a4986"
                  fontStyle="normal"
                  padding="5px"
                  fontWeight="700"
                  textAlign={invoice?.company?.id ? "left" : "center"}
                  vertical-align="middle"
                  borderRight="2px solid rgb(118, 118, 118)"
                >
                  {invoice?.company?.id ? (
                    <Anchor
                      className="invoice-list__link"
                      url={`/company/view/${invoice?.company?.id}/general-info`}
                      target="_blank"
                      styles={{ fontWeight: "700" }}
                    >
                      {`#${invoice.company.id} - ${invoice.company.name}`}
                    </Anchor>
                  ) : (
                    "-"
                  )}
                </GridSubItem>
              )}
              {entityType !== "site" && (
                <GridSubItem
                  color="#0a4986"
                  fontStyle="normal"
                  padding="5px"
                  fontWeight="700"
                  textAlign={invoice?.site?.id ? "left" : "center"}
                  vertical-align="middle"
                  borderRight="2px solid rgb(118, 118, 118)"
                >
                  {invoice?.site?.id ? (
                    <Anchor
                      className="invoice-list__link"
                      url={`/site/view/${invoice?.site?.id}/general-info`}
                      target="_blank"
                      styles={{ fontWeight: "700" }}
                    >
                      {`#${invoice.site.id} - ${invoice.site.name}`}
                    </Anchor>
                  ) : (
                    "-"
                  )}
                </GridSubItem>
              )}
              <GridSubItem
                color="#474747"
                fontStyle="normal"
                padding="5px"
                fontWeight="400"
                textAlign="center"
                vertical-align="middle"
                borderRight="2px solid rgb(118, 118, 118)"
              >
                {invoice?.invoiceDate && ![0, 1, 9, 10].includes(invoice.status)
                  ? getDateFormat(invoice?.invoiceDate, "YYYY-MM-DD")
                  : "-"}
              </GridSubItem>
              <GridSubItem
                color="#0a4986"
                fontStyle="normal"
                padding="5px"
                fontWeight="400"
                vertical-align="middle"
                textAlign="center"
                borderRight="2px solid rgb(118, 118, 118)"
              >
                {invoice?.dueDate
                  ? getDateFormat(invoice?.dueDate, "YYYY-MM-DD")
                  : "-"}
              </GridSubItem>
              <GridSubItem
                color="#0a4986"
                fontStyle="normal"
                padding="5px"
                fontWeight="700"
                textAlign="right"
                vertical-align="middle"
                borderRight="2px solid rgb(118, 118, 118)"
              >
                {renderPriceWithCurrencyWithoutUnit(
                  Number(invoice.totalExcludedTaxes ?? 0).toFixed(2),
                  invoice.currency.symbol,
                  invoice.currency.position
                )}
              </GridSubItem>
              <GridSubItem
                color="#0a4986"
                fontStyle="normal"
                padding="5px"
                fontWeight="700"
                textAlign="center"
                vertical-align="middle"
                borderRight="2px solid rgb(118, 118, 118)"
              >
                {" - "}
              </GridSubItem>
              <GridSubItem
                color="#474747"
                fontStyle="normal"
                padding="5px"
                fontWeight="400"
                textAlign="center"
                vertical-align="middle"
                borderRight="2px solid rgb(118, 118, 118)"
              >
                <ColumnButton
                  border={invoiceStatusColor[invoice.status]}
                  background={invoiceStatusColor[invoice.status]}
                  marginRight="10px"
                >
                  <span>{t(invoiceStatusText[invoice.status])}</span>
                </ColumnButton>
              </GridSubItem>
              <GridSubItem
                color="#474747"
                fontStyle="normal"
                padding="5px"
                fontWeight="400"
                textAlign="center"
                vertical-align="middle"
              >
                <Image
                  src={viewIcon}
                  className="invoice-list__action-icon"
                  onClick={() => onInvoiceView(invoice?.id)}
                  alt={t("GENERIC_ACTION_TOOLTIP_VIEW")}
                  title={t("GENERIC_ACTION_TOOLTIP_VIEW")}
                />
                {[0, 1].includes(invoice.status) && !!invoice.isActive && (
                  <Image
                    src={editIcon}
                    className="invoice-list__action-icon"
                    onClick={() => onInvoiceEdit(invoice?.id)}
                    alt={t("GENERIC_ACTION_TOOLTIP_EDIT")}
                    title={t("GENERIC_ACTION_TOOLTIP_EDIT")}
                  />
                )}
                {!!invoice.isDocumentAvailable && (
                  <Image
                    src={downloadIcon}
                    className="invoice-list__action-icon"
                    onClick={() => onInvoiceDownload(invoice?.id)}
                    alt={t("GENERIC_ACTION_TOOLTIP_DOWNLOAD")}
                    title={t("GENERIC_ACTION_TOOLTIP_DOWNLOAD")}
                  />
                )}
                {[0, 1].includes(invoice.status) && !!invoice.isActive && (
                  <Image
                    src={deleteIcon}
                    className="invoice-list__action-icon"
                    onClick={() => handleDisable(invoice?.id)}
                    alt={t("GENERIC_ACTION_TOOLTIP_DELETE")}
                    title={t("GENERIC_ACTION_TOOLTIP_DELETE")}
                  />
                )}
              </GridSubItem>
            </GridRow>
          );
        })}
      {isConfirmVisible && (
        <WarningPopUp
          title={t("CLIENT_INVOICING_CONFIRMATION_BOX_DISABLE")}
          message={t("CLIENT_INVOICING_CONFIRMATION_BOX_DISABLE_CONTENT")}
          onPopUpCancel={onPopUpCancel}
          onPopUpConfirm={onPopUpConfirm}
          ValidateContent={t("YES")}
          CancelContent={t("CANCEL")}
        />
      )}
    </>
  );
};

ListingData.propTypes = {
  t: PropTypes.func.isRequired,
  page: PropTypes.string,
  entityType: PropTypes.string,
  entityId: PropTypes.number,
};

ListingData.defaultProps = {
  page: "main",
  entityType: "main",
  entityId: null,
};

export default withTranslation()(ListingData);
